import {
  POI_SHIELD_PENDING_SEC,
  NETWORK_CONFIG,
  type NetworkName,
  TransactionHistoryItem,
  RailgunWalletBalanceBucket,
  TransactionHistoryItemCategory
} from '@railgun-community/shared-models'
import { RailgunShieldTxRecord } from '@web/utils/railgunShieldTxStorage'
import { translation } from '@web/utils/safeTranslation'

const allNetworks = Object.values(NETWORK_CONFIG)

/** After shield wait window ends, show "Completing soon" only within this grace; beyond → POI sync message. */
export const SHIELD_POI_COMPLETING_SOON_GRACE_SEC = 15 * 60

export type PrivateHistoryItem = TransactionHistoryItem & {
  networkName: NetworkName
}

export function getPrivateHistoryItemBalanceBucket(
  item: PrivateHistoryItem
): RailgunWalletBalanceBucket {
  const allReceiveTypeAmounts = [...item.receiveERC20Amounts, ...item.receiveNFTAmounts]
  const foundBucket = allReceiveTypeAmounts.find((receive) => receive.balanceBucket)?.balanceBucket

  return foundBucket ?? RailgunWalletBalanceBucket.Spendable
}

/** Normalize a timestamp that may be Unix seconds or milliseconds (e.g. history vs local log). */
export function toUnixMs(rawTimestamp: number): number {
  return rawTimestamp > 1e12 ? rawTimestamp : rawTimestamp * 1000
}

/**
 * Seconds remaining for a fixed-duration wait window from a start instant.
 * `rawStartTimestamp` may be seconds or ms (see {@link toUnixMs}).
 * Result can be negative after the window ends.
 */
export function computeSecondsRemaining(
  waitDurationSec: number,
  rawStartTimestamp: number
): number {
  const startMs = toUnixMs(rawStartTimestamp)
  const elapsedSec = (Date.now() - startMs) / 1000
  return waitDurationSec - elapsedSec
}

/**
 * Compact UI label from seconds remaining (minutes only).
 * Wrapped in parentheses, e.g. `(~45 mins)` or `(~<1 min)`.
 */
export function formatApproxMinutesRemainingLabel(secondsRemaining: number): string {
  // if (secondsRemaining <= 0) {
  //   if (secondsRemaining > -SHIELD_POI_COMPLETING_SOON_GRACE_SEC) {
  //     return '(~Completing soon)'
  //   }
  //   return '(~Waiting for Private Proof of Innocence)'
  // }

  if (secondsRemaining <= 0) return '(~Completing soon)'

  if (secondsRemaining < 60) return '(~<1 min)'

  const minutesLeft = Math.round(secondsRemaining / 60)
  return `(~${minutesLeft} min${minutesLeft > 1 ? 's' : ''})`
}

export const getWaitTimeForShieldPending = (networkName: NetworkName) => {
  const network = allNetworks.find((network) => network.name === networkName)

  const buffer = 60

  if (network) {
    return POI_SHIELD_PENDING_SEC + buffer
  }

  return undefined
}

// TODO: Requires further refinement for other cases
export const getRailgunTxMessage = (options: {
  txid: string
  timestamp: number | undefined
  networkName: NetworkName
  balanceBucket: RailgunWalletBalanceBucket
  savedTxLog: RailgunShieldTxRecord[]
  poiPending: boolean
  category: TransactionHistoryItemCategory
}): string | null => {
  const { txid, timestamp, networkName, balanceBucket, savedTxLog, poiPending, category } = options
  const record = savedTxLog.find((r) => r.txHash === txid)

  if (!poiPending) {
    return null
  }

  if (balanceBucket === RailgunWalletBalanceBucket.ShieldBlocked) {
    return translation.t(
      'Shield confirmed on-chain, but the Private Proof of Innocence list blocked it. The funds are shielded but not yet private-spendable — check PPOI.info for details.'
    )
  }

  if (balanceBucket === RailgunWalletBalanceBucket.MissingExternalPOI) {
    return translation.t(
      'Waiting for Private Proof of Innocence to be generated by the sender. If you sent this from another of your accounts, open ZKX on that account to finish it.'
    )
  }

  if (poiPending) {
    if (balanceBucket === RailgunWalletBalanceBucket.ShieldPending) {
      const waitTimeSec = getWaitTimeForShieldPending(networkName)
      if (waitTimeSec === undefined) return null
      const rawStart = timestamp ?? record?.timestamp
      if (rawStart === undefined) return null
      const timeLeftSec = computeSecondsRemaining(waitTimeSec, rawStart)
      if (timeLeftSec <= 0) {
        if (timeLeftSec > -SHIELD_POI_COMPLETING_SOON_GRACE_SEC) {
          return translation.t('Completing soon')
        }
        return translation.t('Waiting for Private Proof of Innocence to be generated')
      }
      const minutesLeft = Math.ceil(timeLeftSec / 60)
      return translation.t('Est. {{count}} mins remaining for your Private Proof of Innocence', {
        count: minutesLeft
      })
    }
    return translation.t('Waiting for Private Proof of Innocence to be generated')
  }

  return null
}
